import React, { memo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  Infinity,
  FileUp,
  History,
  LineChart,
  Star,
  Check,
  Crown,
  Zap,
  ArrowRight,
  Coins,
  TrendingUp,
  Package,
  CreditCard,
  RefreshCcw
} from 'lucide-react';
import { getBrazilianStatus, isBrazilianUser } from '../../utils/detectBrazil';
import { getUserLanguage, SUPPORTED_LANGUAGES } from '../../utils/detectLanguage';
import { upgradeToProPtBR } from '../../components/mappings/upgradeToPro.pt-br';
import { upgradeToProES } from '../../components/mappings/upgradeToPro.es';
import { starCoinsClient } from '../../services/starCoinsClient';
import { useUsageLimiter } from '../../utils/usageLimiter';
import { getAuth } from 'firebase/auth';
import { trackInitiateStarCoinPurchase } from '../../utils/pixels';

// Memoized feature card for performance
const FeatureCard = memo(({ icon: Icon, title, description }) => (
  <div className="bg-white/[0.02] backdrop-blur-sm rounded-lg p-4 sm:p-5 md:p-6 border border-white/[0.05] hover:border-white/[0.08] transition-all group hover:bg-white/[0.03]">
    <div className="flex items-start gap-3 sm:gap-4">
      <div className="flex-shrink-0">
        <div className="p-2 bg-gradient-to-r from-emerald-500/[0.08] to-violet-500/[0.08] rounded border border-emerald-500/20">
          <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-400/70 group-hover:text-emerald-400/90 transition-colors" />
        </div>
      </div>
      <div className="flex-grow">
        <h3 className="text-white/90 font-medium text-[12px] sm:text-[13px] mb-1.5 uppercase tracking-[0.15em] font-mono">
          {title}
        </h3>
        <p className="text-white/40 text-[10px] sm:text-[11px] leading-relaxed font-mono">
          {description}
        </p>
      </div>
    </div>
  </div>
));

// StarCoin benefit card - Slim premium design
const StarCoinBenefit = memo(({ title, description }) => (
  <div className="bg-gradient-to-br from-amber-900/10 to-orange-900/10 backdrop-blur-sm rounded-lg p-4 border border-amber-700/20 hover:border-amber-600/40 transition-all">
    <h4 className="text-amber-300/90 font-medium text-sm mb-1.5 tracking-wide">{title}</h4>
    <p className="text-slate-400 text-xs leading-relaxed">{description}</p>
  </div>
));

// Professional benefit item
const BenefitItem = memo(({ text }) => (
  <div className="flex items-start gap-2">
    <div className="flex-shrink-0 mt-0.5">
      <div className="w-1.5 h-1.5 bg-emerald-400/60 rounded-full" />
    </div>
    <span className="text-white/50 text-[10px] sm:text-[11px] font-mono">{text}</span>
  </div>
));

// StarCoins Package Card Component
const StarCoinsPackageCard = memo(({ pkg, isSelected, onSelect, currency, currencySymbol, tier, t, isMobile }) => {
  const bonusAmount = pkg.proBonus && tier === 'pro' ? pkg.amount - (pkg.originalAmount || pkg.amount * 0.8) : 0;
  const displayPrice = currency === 'brl' 
    ? (pkg.brlPrice || Math.round(pkg.price * 5.5))
    : pkg.price;
  
  // Mobile-optimized horizontal layout
  if (isMobile) {
    return (
      <div 
        onClick={() => onSelect(pkg.id)}
        className={`relative p-3 rounded-lg cursor-pointer transition-all ${
          isSelected 
            ? 'bg-gradient-to-br from-amber-500/30 to-orange-500/30 border-2 border-amber-500 shadow-lg shadow-amber-500/20' 
            : 'bg-slate-800/50 border border-amber-600/30'
        }`}
      >
        {pkg.popular && (
          <div className="absolute -top-2 right-2 px-2 py-0.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] font-bold rounded-full">
            {t("POPULAR")}
          </div>
        )}
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center">
              <Star className="w-4 h-4 text-amber-400 mr-1" />
              <span className="text-lg font-bold text-amber-300">
                {pkg.amount >= 1000 ? `${pkg.amount/1000}k` : pkg.amount}
              </span>
            </div>
            {bonusAmount > 0 && (
              <div className="text-emerald-400 text-xs font-semibold">
                +{Math.round(bonusAmount)}
              </div>
            )}
          </div>
          
          <div className="text-right">
            {currency === 'brl' && displayPrice === 2.00 ? (
              <div>
                <div className="text-sm text-gray-400 line-through">
                  $5.00
                </div>
                <div className="text-xl font-bold text-white">
                  {currencySymbol}{displayPrice.toFixed(2)}
                </div>
              </div>
            ) : (
              <div className="text-xl font-bold text-white">
                {currencySymbol}{displayPrice.toFixed(2)}
              </div>
            )}
          </div>
          
          {isSelected && (
            <div className="ml-2">
              <div className="w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center">
                <Check className="w-3 h-3 text-white" />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
  
  // Desktop/tablet layout (original)
  return (
    <div 
      onClick={() => onSelect(pkg.id)}
      className={`relative p-4 sm:p-5 md:p-6 rounded-xl cursor-pointer transition-all transform hover:scale-[1.02] ${
        isSelected 
          ? 'bg-gradient-to-br from-amber-500/30 to-orange-500/30 border-2 border-amber-500 shadow-xl shadow-amber-500/20' 
          : 'bg-slate-800/50 border border-amber-600/30 hover:border-amber-500/50'
      }`}
    >
      {pkg.popular && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold rounded-full">
          {t("MOST POPULAR")}
        </div>
      )}
      
      <div className="flex flex-col items-center text-center">
        <div className="flex items-center mb-2">
          <Star className="w-6 h-6 sm:w-7 sm:h-7 text-amber-400 mr-2" />
          <span className="text-2xl sm:text-3xl font-bold text-amber-300">
            {pkg.amount >= 1000 ? `${pkg.amount/1000}k` : pkg.amount}
          </span>
        </div>
        
        {bonusAmount > 0 && (
          <div className="text-emerald-400 text-sm font-semibold mb-2">
            +{Math.round(bonusAmount)} {t("bonus coins")}
          </div>
        )}
        
        {currency === 'brl' && displayPrice === 2.00 ? (
          <div>
            <div className="text-base text-gray-400 line-through">
              $5.00
            </div>
            <div className="text-3xl sm:text-4xl font-bold text-white mb-1">
              {currencySymbol}{displayPrice.toFixed(2)}
            </div>
          </div>
        ) : (
          <div className="text-3xl sm:text-4xl font-bold text-white mb-1">
            {currencySymbol}{displayPrice.toFixed(2)}
          </div>
        )}
        
        {isSelected && (
          <div className="absolute top-3 right-3">
            <div className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center">
              <Check className="w-4 h-4 text-white" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

// Main component
const UpgradeToPro = () => {
  const navigate = useNavigate();
  const isBrazilian = getBrazilianStatus();
  const auth = getAuth();
  const userId = auth.currentUser?.uid;
  const { tier, starCoins } = useUsageLimiter(userId);
  
  // Star Coins purchase state
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [purchaseError, setPurchaseError] = useState(null);
  const [currency, setCurrency] = useState('usd');
  const [showCurrencyOption, setShowCurrencyOption] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Helper function to get translated text
  const t = (key) => {
    const userLanguage = getUserLanguage();
    if (userLanguage === SUPPORTED_LANGUAGES.PT_BR) {
      return upgradeToProPtBR[key] || key;
    }
    if (userLanguage === SUPPORTED_LANGUAGES.ES || userLanguage === SUPPORTED_LANGUAGES.ES_MX) {
      return upgradeToProES[key] || key;
    }
    return key; // Default English
  };

  const starCoinBenefits = [
    {
      icon: Coins,
      title: t("Pay As You Go"),
      description: t("Only pay for what you use. No monthly commitments required.")
    },
    {
      icon: Zap,
      title: t("Instant Access"),
      description: t("Get immediate access to all AI features without subscription.")
    },
    {
      icon: TrendingUp,
      title: t("Flexible Usage"),
      description: t("Scale up or down based on your betting analysis needs.")
    },
    {
      icon: Package,
      title: t("Bulk Savings"),
      description: t("Save more with larger StarCoin packages. Pro members save up to 50%!")
    }
  ];

  const features = [
    {
      icon: Infinity,
      title: t("Extended Access & Priority Processing"),
      description: t("Enjoy significantly higher usage limits with priority queue processing for instant responses. Never wait for AI-powered insights when you need them most.")
    },
    {
      icon: History,
      title: t("Complete Conversation History"),
      description: t("Access your entire conversation archive with advanced search capabilities. Find specific insights, strategies, and recommendations from any previous session.")
    },
    {
      icon: FileUp,
      title: t("Advanced Data Analysis Tools"),
      description: t("Upload custom datasets, spreadsheets, and documents for personalized analysis. Transform your raw data into actionable sports betting intelligence.")
    },
    {
      icon: LineChart,
      title: t("Personalized AI Recommendations"),
      description: t("Receive tailored insights based on your betting patterns, preferences, and historical performance. Our AI learns your style to optimize recommendations.")
    }
  ];

  const benefits = [
    t("Priority customer support with dedicated assistance"),
    t("Early access to new features and beta releases"),
    t("Advanced analytics dashboard with performance tracking"),
    t("Custom prompt templates for specialized analysis"),
    t("Export capabilities for reports and insights"),
    t("Enhanced security with premium data protection")
  ];

  // Load Star Coins packages and detect mobile
  useEffect(() => {
    const loadPackages = async () => {
      try {
        const isBrazil = isBrazilianUser();
        setShowCurrencyOption(isBrazil);
        if (isBrazil) {
          setCurrency('brl');
        }
        
        const packagesData = await starCoinsClient.getPackages(tier === 'pro');
        setPackages(packagesData);
        if (packagesData.length > 0) {
          // Select the popular package by default
          const popularPkg = packagesData.find(p => p.popular) || packagesData[1];
          setSelectedPackage(popularPkg?.id);
        }
      } catch (error) {
        console.error('Error loading packages:', error);
      }
    };
    
    // Check if mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640); // sm breakpoint
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    loadPackages();
    
    return () => window.removeEventListener('resize', checkMobile);
  }, [tier]);

  // Handle Star Coins purchase
  const handlePurchase = async () => {
    if (!selectedPackage || !userId) {
      if (!userId) {
        // User not logged in, redirect to login
        navigate('/login');
        return;
      }
      return;
    }
    
    setIsProcessing(true);
    setPurchaseError(null);
    
    try {
      const packageData = packages.find(p => p.id === selectedPackage);
      if (!packageData) {
        throw new Error('Package not found');
      }
      
      // Use custom BRL price if it exists, otherwise convert at 5.5x
      let price = packageData.price;
      if (currency === 'brl') {
        price = packageData.brlPrice || Math.round(packageData.price * 5.5);
      }
      
      // Track initiate checkout event for Facebook Pixel
      trackInitiateStarCoinPurchase(packageData.id, price);
      
      // Create checkout session
      await starCoinsClient.createCheckout(
        packageData.id,
        packageData.amount,
        price,
        tier === 'pro',
        currency
      );
    } catch (error) {
      console.error('Error processing payment:', error);
      setIsProcessing(false);
      setPurchaseError(t('Error processing payment. Please try again.'));
    }
  };

  // Add floating particles animation
  useEffect(() => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.style.position = 'fixed';
    canvas.style.top = '0';
    canvas.style.left = '0';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.pointerEvents = 'none';
    canvas.style.zIndex = '1';
    document.body.appendChild(canvas);

    let particles = [];
    const particleCount = 100;

    const createParticles = () => {
      particles = [];
      for (let i = 0; i < particleCount; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          size: Math.random() * 2 + 1,
          speedX: (Math.random() - 0.5) * 0.5,
          speedY: (Math.random() - 0.5) * 0.5,
          color: i % 2 === 0 
            ? `rgba(16, 185, 129, ${Math.random() * 0.5})`  // Emerald
            : `rgba(251, 191, 36, ${Math.random() * 0.3})`  // Amber
        });
      }
    };

    const animateParticles = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(particle => {
        particle.x += particle.speedX;
        particle.y += particle.speedY;

        if (particle.x < 0 || particle.x > canvas.width) particle.speedX *= -1;
        if (particle.y < 0 || particle.y > canvas.height) particle.speedY *= -1;

        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fillStyle = particle.color;
        ctx.fill();
      });
      requestAnimationFrame(animateParticles);
    };

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      createParticles();
    };

    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();
    animateParticles();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      document.body.removeChild(canvas);
    };
  }, []);

  return (
    <div className="min-h-screen bg-black/95 overflow-hidden">
      {/* Background animation - floating particles */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0" />

      {/* Main content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 py-8 md:py-16 lg:py-24">
        
        {/* Hero section - Terminal Style */}
        <div className="text-center mb-8 md:mb-12">
          <div className="inline-flex items-center justify-center mb-4 md:mb-6">
            <div className="p-3 bg-gradient-to-r from-amber-500/[0.08] to-amber-600/[0.08] rounded border border-amber-500/20">
              <Star className="w-6 h-6 md:w-8 md:h-8 text-amber-400/70" />
            </div>
          </div>

          <h1 className="text-[24px] sm:text-[32px] md:text-[40px] font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-500 mb-3 leading-tight uppercase tracking-[0.15em]">
            {t("StarCoins")}
            <span className="block text-[14px] sm:text-[16px] md:text-[18px] text-white/50 font-mono tracking-wider mt-2">{t("Pay-Per-Use AI")}</span>
          </h1>

          <p className="text-white/40 text-[11px] sm:text-[12px] md:text-[13px] mb-6 md:mb-8 max-w-2xl mx-auto leading-relaxed font-mono px-4">
            {isBrazilian
              ? (
                <>
                  StarCoins são a moeda virtual para uso estendido que nunca expira. Não é necessário assinatura para jogar casualmente, mas oferecemos assinatura Pro para usuários avançados com descontos em moedas! As moedas podem ser usadas para chats extras, giros, quickpicks, ou em nosso{' '}
                  <button
                    onClick={() => navigate('/market')}
                    className="inline text-violet-400 hover:text-violet-300 transition-colors underline font-semibold"
                  >
                    {t("market for advanced gamers")}
                  </button>
                  .
                </>
              )
              : (
                <>
                  StarCoins are the virtual currency for extended usage that never expire. No subscription needed for casual play, but we offer Pro subscriptions for power users with coin discounts! Use coins for extra chats, spins, quickpicks, or in our{' '}
                  <button
                    onClick={() => navigate('/market')}
                    className="inline text-violet-400 hover:text-violet-300 transition-colors underline font-semibold"
                  >
                    market for advanced gamers
                  </button>
                  .
                </>
              )}
          </p>
        </div>

        {/* Star Coins section - Terminal Style with Amber Accents */}
        <div className="mb-8 md:mb-12">
          <div className="bg-gradient-to-r from-amber-500/[0.03] to-amber-600/[0.03] backdrop-blur-sm rounded-lg p-6 sm:p-8 md:p-10 border border-amber-500/20">
            <div className="text-center mb-6 md:mb-8">
              <h2 className="text-[16px] sm:text-[20px] md:text-[24px] font-bold text-amber-400/90 uppercase tracking-[0.2em] font-mono">
                {t("StarCoins System")}
              </h2>

              {/* Star Coins Purchase Options - MOVED TO TOP */}
              <div className="mb-8 md:mb-10">
                <h3 className="text-[13px] sm:text-[14px] font-medium text-amber-400/70 mb-4 text-center uppercase tracking-[0.25em] font-mono">
                  {t("Choose Your Package")}
                </h3>
                
                {/* Currency Selector for Brazilian users */}
                {showCurrencyOption && (
                  <div className="mb-6 flex justify-center">
                    <div className="inline-flex rounded-lg bg-slate-800/50 p-1">
                      <button
                        onClick={() => setCurrency('usd')}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                          currency === 'usd'
                            ? 'bg-blue-500/30 text-blue-300'
                            : 'text-white/60 hover:text-white/80'
                        }`}
                      >
                        USD ($)
                      </button>
                      <button
                        onClick={() => setCurrency('brl')}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                          currency === 'brl'
                            ? 'bg-green-500/30 text-green-300'
                            : 'text-white/60 hover:text-white/80'
                        }`}
                      >
                        BRL (R$)
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Packages Grid */}
                <div className={`${isMobile ? 'space-y-2' : 'grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6'} mb-6`}>
                  {packages.map((pkg) => (
                    <StarCoinsPackageCard
                      key={pkg.id}
                      pkg={pkg}
                      isSelected={selectedPackage === pkg.id}
                      onSelect={setSelectedPackage}
                      currency={currency}
                      currencySymbol={currency === 'brl' ? 'R$' : '$'}
                      tier={tier}
                      t={t}
                      isMobile={isMobile}
                    />
                  ))}
                </div>
                
                {/* Error Message */}
                {purchaseError && (
                  <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
                    <p className="text-red-400 text-sm text-center">{purchaseError}</p>
                  </div>
                )}
                
                {/* Purchase Button */}
                <div className="flex justify-center">
                  <button
                    onClick={handlePurchase}
                    disabled={!selectedPackage || isProcessing}
                    className="px-8 sm:px-10 py-3 bg-gradient-to-r from-amber-500/20 to-amber-600/20 border border-amber-500/40 hover:border-amber-400/60 disabled:from-gray-600/20 disabled:to-gray-700/20 disabled:border-gray-600/20 disabled:cursor-not-allowed text-amber-400/90 hover:text-amber-400 disabled:text-gray-500 font-medium rounded-lg transition-all text-[12px] sm:text-[13px] flex items-center uppercase tracking-wider font-mono disabled:hover:border-gray-600/20"
                  >
                    {isProcessing ? (
                      <>
                        <RefreshCcw className="w-4 h-4 mr-2 animate-spin" />
                        {t("Processing...")}
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" />
                        {t("Purchase Star Coins")}
                      </>
                    )}
                  </button>
                </div>
                
                {/* Secure Checkout Note */}
                <p className="text-center text-white/20 text-[9px] mt-2 font-mono uppercase tracking-wider">
                  {t("Secure checkout powered by Stripe")}
                </p>
                
                {/* Balance Display if logged in */}
                {userId && !starCoins?.loading && (
                  <div className="mt-4 text-center">
                    <p className="text-white/30 text-[10px] font-mono uppercase tracking-wider">
                      {t("Current Balance")}:
                      <span className="text-amber-400/70 font-semibold ml-2">
                        <Star className="inline w-3 h-3 mr-1" />
                        {starCoins?.balance || 0} {t("Star Coins")}
                      </span>
                    </p>
                  </div>
                )}
              </div>

              {/* Star Coins Usage Breakdown - Terminal Style */}
              <div className="bg-white/[0.02] backdrop-blur-sm rounded-lg p-4 sm:p-6 mb-6 md:mb-8 border border-white/[0.05]">
                <h3 className="text-[12px] sm:text-[13px] font-medium text-amber-400/70 mb-3 text-center uppercase tracking-[0.25em] font-mono">
                  {t("How Star Coins Work")}
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4 mb-4">
                  <div className="text-center p-3 bg-emerald-500/[0.05] rounded border border-emerald-500/10">
                    <div className="text-[20px] sm:text-[24px] font-bold text-emerald-400/80 mb-1 font-mono">7</div>
                    <div className="text-[10px] sm:text-[11px] text-white/40 font-mono">{t("Star Coins")}</div>
                    <div className="text-[9px] sm:text-[10px] text-emerald-400/60 mt-0.5 font-mono">{t("= 1 QuickPick")}</div>
                  </div>
                  <div className="text-center p-3 bg-blue-500/[0.05] rounded border border-blue-500/10">
                    <div className="text-[20px] sm:text-[24px] font-bold text-blue-400/80 mb-1 font-mono">9</div>
                    <div className="text-[10px] sm:text-[11px] text-white/40 font-mono">{t("Star Coins")}</div>
                    <div className="text-[9px] sm:text-[10px] text-blue-400/60 mt-0.5 font-mono">{t("= 1 xPicks")}</div>
                  </div>
                  <div className="text-center p-3 bg-amber-500/[0.05] rounded border border-amber-500/10">
                    <div className="text-[20px] sm:text-[24px] font-bold text-amber-400/80 mb-1 font-mono">11</div>
                    <div className="text-[10px] sm:text-[11px] text-white/40 font-mono">{t("Star Coins")}</div>
                    <div className="text-[9px] sm:text-[10px] text-amber-400/60 mt-0.5 font-mono">{t("= 1 AI Message")}</div>
                  </div>
                  <div className="text-center p-3 bg-violet-500/[0.05] rounded border border-violet-500/10">
                    <div className="text-[20px] sm:text-[24px] font-bold text-violet-400/80 mb-1 font-mono">∞</div>
                    <div className="text-[10px] sm:text-[11px] text-white/40 font-mono">{t("Never Expire")}</div>
                    <div className="text-[9px] sm:text-[10px] text-violet-400/60 mt-0.5 font-mono">{t("Use anytime!")}</div>
                  </div>
                </div>
                <p className="text-center text-white/30 text-[10px] sm:text-[11px] font-mono">
                  {t("Use PickStar for your sports betting research now or save for later – your Star Coins never expire!")}
                </p>
              </div>

              {/* StarCoin Benefits Grid */}
              <div className="grid sm:grid-cols-2 gap-4 md:gap-6 mb-8 md:mb-10">
                {starCoinBenefits.map((benefit, index) => (
                  <StarCoinBenefit key={index} {...benefit} />
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center">
                <button
                  onClick={handlePurchase}
                  disabled={!selectedPackage || isProcessing}
                  className="px-6 sm:px-8 py-2.5 sm:py-3 bg-gradient-to-r from-amber-500/20 to-amber-600/20 border border-amber-500/40 hover:border-amber-400/60 disabled:from-gray-600/20 disabled:to-gray-700/20 disabled:border-gray-600/20 disabled:cursor-not-allowed text-amber-400/90 hover:text-amber-400 disabled:text-gray-500 font-medium rounded-lg transition-all text-[11px] sm:text-[12px] flex items-center justify-center uppercase tracking-wider font-mono disabled:hover:border-gray-600/20"
                >
                  {isProcessing ? (
                    <>
                      <RefreshCcw className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      {t("Processing...")}
                    </>
                  ) : (
                    <>
                      <Star className="w-3.5 h-3.5 mr-1.5" />
                      {t("Buy StarCoins Now")}
                      <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                    </>
                  )}
                </button>
                <button
                  onClick={() => navigate('/chat')}
                  className="px-6 sm:px-8 py-2.5 sm:py-3 bg-white/[0.02] border border-white/[0.05] hover:bg-white/[0.03] hover:border-white/[0.08] text-white/50 hover:text-white/70 font-medium rounded-lg transition-all text-[11px] sm:text-[12px] uppercase tracking-wider font-mono"
                >
                  {t("Learn More")}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* TEMPORARILY DISABLED - Pro Subscription section */}
        {/* <div className="mb-12 md:mb-20">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 md:mb-4 px-2">{t("Want Even More Value?")}</h2>
            <p className="text-emerald-100/70 text-sm sm:text-base md:text-lg max-w-2xl mx-auto px-2">
              {t("Upgrade to Pro for built-in usage, discounted StarCoins, and exclusive features")}
            </p>
          </div>

          <div className="bg-slate-800/30 backdrop-blur-lg rounded-xl md:rounded-2xl p-6 sm:p-8 md:p-12 border border-emerald-800/30">
            <div className="grid md:grid-cols-2 gap-6 md:gap-8 items-center">
              <div>
                <div className="flex items-center gap-2 sm:gap-3 mb-4 md:mb-6">
                  <Crown className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-emerald-300" />
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white">{t("Pro Membership")}</h3>
                </div>
                <ul className="space-y-3 sm:space-y-4 mb-6 md:mb-8">
                  <BenefitItem text={t("4x built-in monthly usage included")} />
                  <BenefitItem text={t("Up to 50% discount on all StarCoin purchases")} />
                  <BenefitItem text={t("Priority AI processing queue")} />
                  <BenefitItem text={t("Exclusive Pro-only features")} />
                  <BenefitItem text={t("Advanced analytics dashboard")} />
                  <BenefitItem text={t("Priority customer support")} />
                </ul>
                <button
                  onClick={() => navigate('/pricing')}
                  className="w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-3 bg-gradient-to-r from-emerald-500/[0.12] to-violet-500/[0.12] border border-emerald-500/30 hover:border-violet-500/40 text-white/90 font-medium rounded-lg transition-all flex items-center justify-center text-[11px] sm:text-[12px] uppercase tracking-wider font-mono"
                >
                  <Crown className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  {t("Upgrade to Pro")}
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
                </button>
              </div>
              <div className="bg-emerald-900/20 rounded-xl p-6 sm:p-8 border border-emerald-700/30 mt-6 md:mt-0">
                <h4 className="text-lg sm:text-xl md:text-2xl font-bold text-emerald-300 mb-3 md:mb-4">{t("Pro Benefits Include:")}</h4>
                <div className="space-y-2 sm:space-y-3 text-slate-300">
                  <p className="flex items-center gap-2 text-sm sm:text-base">
                    <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-400 flex-shrink-0" />
                    {t("$50 worth of usage every month")}
                  </p>
                  <p className="flex items-center gap-2 text-sm sm:text-base">
                    <Coins className="w-4 h-4 sm:w-5 sm:h-5 text-amber-400 flex-shrink-0" />
                    {t("Buy StarCoins at UP TO 50% off regular price")}
                  </p>
                  <p className="flex items-center gap-2 text-sm sm:text-base">
                    <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-400 flex-shrink-0" />
                    {t("Never worry about running out mid-analysis")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div> */}

        {/* Pro Coming Soon Section - TEMPORARY */}
        <div className="mb-12 md:mb-20">
          <div className="bg-gradient-to-r from-emerald-900/20 to-violet-900/20 backdrop-blur-lg rounded-xl md:rounded-2xl p-8 sm:p-12 md:p-16 border border-emerald-700/30 text-center">
            <div className="max-w-2xl mx-auto">
              <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-emerald-500/10 rounded-full mb-6">
                <Crown className="w-8 h-8 sm:w-10 sm:h-10 text-emerald-400" />
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
                {t("Pro Subscriptions Coming Soon!")}
              </h2>
              <p className="text-emerald-100/70 text-base sm:text-lg md:text-xl mb-6">
                {t("We're working on something special for power users. Stay tuned for exclusive Pro features and discounts!")}
              </p>
              <p className="text-white/50 text-sm sm:text-base">
                {t("In the meantime, enjoy unlimited access with StarCoins - pay only for what you use, no subscription needed!")}
              </p>
            </div>
          </div>
        </div>

        {/* TEMPORARILY DISABLED - Features grid */}
        {/* <div className="mb-12 md:mb-20">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 md:mb-4 px-2">{t("Premium Features")}</h2>
            <p className="text-emerald-100/70 text-sm sm:text-base md:text-lg max-w-2xl mx-auto px-2">
              {t("Advanced capabilities available with both StarCoins and Pro membership")}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
            {features.map((feature, index) => (
              <FeatureCard key={index} {...feature} />
            ))}
          </div>
        </div> */}

        {/* TEMPORARILY DISABLED - Additional Benefits */}
        {/* <div className="mb-12 md:mb-20">
          <div className="bg-slate-800/30 backdrop-blur-lg rounded-xl md:rounded-2xl p-6 sm:p-8 md:p-12 border border-emerald-800/30">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-6 md:mb-10">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3 md:mb-4">{t("Additional Pro Benefits")}</h2>
                <p className="text-emerald-100/70 text-sm sm:text-base md:text-lg">
                  {t("Everything you need for professional-grade sports analytics and betting intelligence.")}
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 sm:gap-5 md:gap-6">
                {benefits.map((benefit, index) => (
                  <BenefitItem key={index} text={benefit} />
                ))}
              </div>
            </div>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default memo(UpgradeToPro);